/**
 * DEMA-WMA Gainer: backtest vs paper trades on one chart, against the DEMA indicator.
 *
 * Read-only. Loads GET /admin/strategies/dema-wma-gainer/chart (stored 5-minute bars, the
 * strategy's indicators, a chosen stored backtest run and the paper record) and offers the same
 * view as a TradingView Pine Script v6 indicator (GET .../chart/pine) to paste into TradingView.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  type DemaWmaBacktestRunSummary,
  type DemaWmaChart,
  type DemaWmaChartSummary,
  type DemaWmaCompareRow,
  type DemaWmaCompareStatus,
  type DemaWmaExitClass,
} from "@/services/demaWmaGainerService";
import { useDemaWmaService } from "@/services/demaWmaServiceContext";
import { isoToEpochSeconds } from "@/components/charts/zonedTime";
import { extractErrorMessage } from "@/utils/errors";
import DemaWmaComparisonChart from "./DemaWmaComparisonChart";
import {
  COLORS,
  COMPARE_STATUS_LABEL,
  DEFAULT_LAYERS,
  EXIT_COLORS,
  EXIT_LABEL,
  buildChartData,
  buildMarkers,
  describeEvent,
  type Layers,
} from "./demaWmaChartData";

const INDICES = ["BANKNIFTY", "NIFTY"] as const;
const EXIT_CLASSES: DemaWmaExitClass[] = ["STOP_LOSS", "TARGET", "WMA_SLOPE", "EOD", "MANUAL", "OTHER"];
const STATUS_COLOR: Record<DemaWmaCompareStatus, "success" | "warning" | "info" | "secondary" | "default"> = {
  MATCHED: "success", EXIT_DIFFERS: "warning", BACKTEST_ONLY: "info", PAPER_ONLY: "secondary", PAPER_OPEN: "default",
};

const num = (v: number | null | undefined, d = 0) => (v === null || v === undefined ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d }));
const money = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `₹${num(v)}`);
const tone = (v: number | null | undefined) => (v === null || v === undefined ? undefined : v >= 0 ? "success.main" : "error.main");
const hm = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—");

function istToday(): string {
  const d = new Date(Date.now() + 330 * 60_000);
  return d.toISOString().slice(0, 10);
}
function minusDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function Dot({ color, shape = "●" }: { color: string; shape?: string }) {
  return <span style={{ color, fontWeight: 700, marginRight: 4 }}>{shape}</span>;
}

function SummaryCard({ title, color, s, extra }: { title: string; color: string; s: DemaWmaChartSummary | null; extra?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%", borderLeft: `4px solid ${color}` }}>
      <Typography variant="subtitle2">{title}</Typography>
      {s ? (
        <>
          <Typography variant="h6" sx={{ color: tone(s.net_pnl) }}>{money(s.net_pnl)}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {s.closed} closed · win {s.win_rate_pct ?? "—"}% · {num(s.index_points, 1)} index pts{s.open ? ` · ${s.open} open` : ""}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {EXIT_CLASSES.filter((c) => s.by_exit_class[c]).map((c) => `${EXIT_LABEL[c]} ${s.by_exit_class[c]}`).join(" · ") || "No exits"}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">{extra ?? "—"}</Typography>
      )}
    </Paper>
  );
}

export default function DemaWmaComparisonCard() {
  const demaWmaGainerService = useDemaWmaService();
  const [index, setIndex] = useState<string>("BANKNIFTY");
  const [toDate, setToDate] = useState(istToday());
  const [fromDate, setFromDate] = useState(minusDays(istToday(), 14));
  const [runs, setRuns] = useState<DemaWmaBacktestRunSummary[]>([]);
  const [backtestId, setBacktestId] = useState<string>("");
  const [chart, setChart] = useState<DemaWmaChart | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [layers, setLayers] = useState<Layers>(DEFAULT_LAYERS);
  const [hoverBar, setHoverBar] = useState<number | null>(null);
  const [focusTime, setFocusTime] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<DemaWmaCompareStatus | "ALL">("ALL");

  useEffect(() => {
    let cancelled = false;
    demaWmaGainerService
      .listBacktestRuns(30)
      .then((r) => {
        if (cancelled) return;
        setRuns(r);
        if (r.length > 0) setBacktestId((cur) => cur || r[0].backtest_id);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [demaWmaGainerService]);

  const query = () => ({ index, from_date: fromDate, to_date: toDate, backtest_id: backtestId || null });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setChart(await demaWmaGainerService.getChart(query()));
      setHoverBar(null);
      setFocusTime(null);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const downloadPine = async () => {
    setDownloading(true);
    setError(null);
    try {
      const { filename, script } = await demaWmaGainerService.getPineScript(query());
      const g = globalThis;
      const url = g.URL.createObjectURL(new g.Blob([script], { type: "text/plain" }));
      const a = g.document.createElement("a");
      a.href = url;
      a.download = filename;
      g.document.body.appendChild(a);
      a.click();
      a.remove();
      g.URL.revokeObjectURL(url);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setDownloading(false);
    }
  };

  const data = useMemo(() => (chart ? buildChartData(chart) : null), [chart]);
  const markers = useMemo(() => (chart && data ? buildMarkers(chart, data, layers) : []), [chart, data, layers]);
  const rows: DemaWmaCompareRow[] = useMemo(
    () => (chart?.comparison?.rows ?? []).filter((r) => statusFilter === "ALL" || r.status === statusFilter),
    [chart, statusFilter],
  );
  const baselineFinal = data && data.baseline.length ? data.baseline[data.baseline.length - 1].value : null;
  const toggle = (k: keyof Omit<Layers, "exits">) => setLayers((l) => ({ ...l, [k]: !l[k] }));
  const toggleExit = (c: DemaWmaExitClass) => setLayers((l) => ({ ...l, exits: { ...l.exits, [c]: !l.exits[c] } }));
  const focus = (iso: string | null | undefined) => {
    const t = isoToEpochSeconds(iso ?? null);
    if (t !== null) setFocusTime(t);
  };

  const hover = chart && hoverBar !== null ? chart.bars[hoverBar] : null;
  const hoverEvents = data && hoverBar !== null ? data.eventsByBar.get(hoverBar) ?? [] : [];
  const ind = chart?.indicator_settings;

  return (
    <Card sx={{ mb: 3 }} data-testid="dema-compare-card">
      <CardContent>
        <Typography variant="h6">Backtest vs paper — chart comparison</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A stored backtest run and the paper-trading record of one index on the same 5-minute candles, with the strategy&apos;s
          DEMA lines and WMA21-of-RSI. Download the TradingView script to see the same markers on a TradingView chart.
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Index" value={index} onChange={(e) => setIndex(e.target.value)}
              inputProps={{ "data-testid": "compare-index" }}>
              {INDICES.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate}
              onChange={(e) => setFromDate(e.target.value)} inputProps={{ "data-testid": "compare-from" }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate}
              onChange={(e) => setToDate(e.target.value)} inputProps={{ "data-testid": "compare-to" }} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth size="small" label="Backtest run" value={backtestId}
              onChange={(e) => setBacktestId(e.target.value)} inputProps={{ "data-testid": "compare-run" }}>
              <MenuItem value="">None (paper only)</MenuItem>
              {runs.map((r) => (
                <MenuItem key={r.backtest_id} value={r.backtest_id}>
                  {r.from_date} → {r.to_date} · {r.pnl_model} · {r.trade_count} trades · v{r.config_version ?? "—"}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => void load()} disabled={loading} data-testid="compare-load">Load chart</Button>
              <Button variant="outlined" onClick={() => void downloadPine()} disabled={downloading} data-testid="compare-pine">
                TradingView script
              </Button>
            </Stack>
          </Grid>
        </Grid>
        {(loading || downloading) && <LinearProgress sx={{ mt: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {chart && data && (
          <Box sx={{ mt: 2 }}>
            {chart.notes.map((n) => <Alert key={n} severity="info" sx={{ mb: 1 }}>{n}</Alert>)}
            {chart.unplaced.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }} data-testid="compare-unplaced">
                {chart.unplaced.length} recorded event(s) are not drawn because their 5-minute bar is not in the stored data:{" "}
                {chart.unplaced.slice(0, 5).join("; ")}{chart.unplaced.length > 5 ? " …" : ""}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <SummaryCard title="Backtest (selected run, this range)" color={COLORS.backtest} s={chart.summary.BACKTEST}
                  extra="No backtest run selected." />
              </Grid>
              <Grid item xs={12} md={4}>
                <SummaryCard title="Paper trading (this range)" color={COLORS.paper} s={chart.summary.PAPER} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1.5, height: "100%", borderLeft: `4px solid ${COLORS.baseline}` }}>
                  <Typography variant="subtitle2">DEMA-trend baseline</Typography>
                  <Typography variant="h6" sx={{ color: tone(baselineFinal) }}>{num(baselineFinal, 1)} index pts</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Always with the DEMA{ind?.dema_fast}/{ind?.dema_slow} trend intraday, flat overnight, no costs — a yardstick
                    for the trend&apos;s move, not a TekInvest strategy. Compare with the index points of the trades.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }} data-testid="compare-layers">
              <Chip size="small" label="Backtest" color={layers.backtest ? "primary" : "default"} onClick={() => toggle("backtest")}
                variant={layers.backtest ? "filled" : "outlined"} />
              <Chip size="small" label="Paper" onClick={() => toggle("paper")} variant={layers.paper ? "filled" : "outlined"}
                sx={layers.paper ? { bgcolor: COLORS.paper, color: "#fff" } : undefined} />
              <Chip size="small" label="Open positions" onClick={() => toggle("open")} variant={layers.open ? "filled" : "outlined"} />
              <Chip size="small" label="Rejected signals" onClick={() => toggle("rejected")} variant={layers.rejected ? "filled" : "outlined"} />
              <Chip size="small" label="Skipped crosses" onClick={() => toggle("skipped")} variant={layers.skipped ? "filled" : "outlined"} />
              {EXIT_CLASSES.map((c) => (
                <Chip key={c} size="small" label={EXIT_LABEL[c]} onClick={() => toggleExit(c)}
                  variant={layers.exits[c] ? "filled" : "outlined"}
                  sx={layers.exits[c] ? { bgcolor: EXIT_COLORS[c], color: "#fff" } : { borderColor: EXIT_COLORS[c] }} />
              ))}
            </Stack>

            <DemaWmaComparisonChart
              data={data}
              markers={markers}
              labels={{
                demaFast: `DEMA ${ind?.dema_fast ?? ""}`, demaSlow: `DEMA ${ind?.dema_slow ?? ""}`,
                rsi: `RSI ${ind?.rsi_period ?? ""}`, wma: `WMA${ind?.rsi_wma ?? ""} of RSI`,
              }}
              showBacktestPoints={layers.backtest && chart.backtest !== null}
              focusTime={focusTime}
              onHoverBar={setHoverBar}
            />

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }} data-testid="compare-legend">
              <Typography variant="caption"><Dot color={COLORS.backtest} shape="▲▼" />Backtest entry CE / PE</Typography>
              <Typography variant="caption"><Dot color={COLORS.paper} shape="▲▼" />Paper entry CE / PE</Typography>
              <Typography variant="caption"><Dot color={COLORS.open} />Paper position still open</Typography>
              <Typography variant="caption">
                <Dot color={EXIT_COLORS.STOP_LOSS} shape="■" />SL <Dot color={EXIT_COLORS.TARGET} shape="■" />Target{" "}
                <Dot color={EXIT_COLORS.WMA_SLOPE} shape="■" />WMA21 <Dot color={EXIT_COLORS.EOD} shape="■" />EOD{" "}
                <Dot color={EXIT_COLORS.MANUAL} shape="■" />Manual / kill-switch (exits: BT above, paper below the bar)
              </Typography>
              <Typography variant="caption"><Dot color={COLORS.backtestRejected} />/<Dot color={COLORS.paperRejected} />Rejected signal</Typography>
              <Typography variant="caption"><Dot color={COLORS.demaFast} shape="━" />DEMA fast <Dot color={COLORS.demaSlow} shape="━" />DEMA slow</Typography>
            </Stack>

            <Paper variant="outlined" sx={{ p: 1, mt: 1, minHeight: 56 }} data-testid="compare-hover">
              {hover ? (
                <>
                  <Typography variant="caption" component="div">
                    <b>{hm(hover.t)}</b> O {hover.o} H {hover.h} L {hover.l} C {hover.c} · DEMA {num(chart.indicators.dema_fast[hoverBar!], 1)} /{" "}
                    {num(chart.indicators.dema_slow[hoverBar!], 1)} (
                    {(chart.indicators.dema_fast[hoverBar!] ?? 0) > (chart.indicators.dema_slow[hoverBar!] ?? 0) ? "fast above slow" : "fast below slow"}) ·
                    WMA {num(chart.indicators.wma[hoverBar!], 2)}
                  </Typography>
                  {hoverEvents.map((e) => <Typography key={e.id} variant="caption" component="div">{describeEvent(e)}</Typography>)}
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">Hover a candle to see its values and every recorded event on it.</Typography>
              )}
            </Paper>

            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              {chart.index} spot, completed {chart.timeframe_minutes}-minute bars, {chart.from_date} to {chart.to_date}, times in IST.{" "}
              {chart.data_quality.bars} bars ({chart.data_quality.invalid_bars} invalid, left blank). Indicators use config v
              {chart.config_version ?? "—"}. Markers are the recorded backtest / paper events, not recalculated; paper exit index price =
              that bar&apos;s close. Bottom pane: cumulative index points (trade direction × index move), same units for all three lines.
            </Typography>

            {chart.comparison && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Trade-by-trade</Typography>
                  <Chip size="small" label={`All ${chart.comparison.rows.length}`} onClick={() => setStatusFilter("ALL")}
                    variant={statusFilter === "ALL" ? "filled" : "outlined"} />
                  {(Object.keys(COMPARE_STATUS_LABEL) as DemaWmaCompareStatus[]).filter((k) => chart.comparison!.counts[k]).map((k) => (
                    <Chip key={k} size="small" color={STATUS_COLOR[k]} label={`${COMPARE_STATUS_LABEL[k]} ${chart.comparison!.counts[k]}`}
                      onClick={() => setStatusFilter(k)} variant={statusFilter === k ? "filled" : "outlined"} />
                  ))}
                  <Typography variant="caption" color="text.secondary">
                    Matched trades: backtest {money(chart.comparison.matched_net_pnl_backtest)} vs paper{" "}
                    {money(chart.comparison.matched_net_pnl_paper)}
                  </Typography>
                </Stack>
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader data-testid="compare-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell><TableCell>Side</TableCell><TableCell>Signal</TableCell>
                        <TableCell>Backtest exit</TableCell><TableCell align="right">BT net</TableCell>
                        <TableCell>Paper exit</TableCell><TableCell align="right">Paper net</TableCell>
                        <TableCell align="right">Paper − BT</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r, k) => (
                        <TableRow key={`${r.backtest?.trade_id ?? ""}-${r.paper?.trade_id ?? ""}-${k}`} hover sx={{ cursor: "pointer" }}
                          onClick={() => focus(r.entry_time)}>
                          <TableCell><Chip size="small" color={STATUS_COLOR[r.status]} label={COMPARE_STATUS_LABEL[r.status]} /></TableCell>
                          <TableCell>{r.side}</TableCell>
                          <TableCell>{hm(r.entry_time)}{r.entry_bar_gap ? ` (±${r.entry_bar_gap} bar)` : ""}</TableCell>
                          <TableCell>{r.backtest ? `${hm(r.backtest.exit_time)} ${r.backtest.exit_reason ?? ""}` : "—"}</TableCell>
                          <TableCell align="right" sx={{ color: tone(r.backtest?.net_pnl) }}>{money(r.backtest?.net_pnl)}</TableCell>
                          <TableCell>
                            {r.paper ? (r.paper.status === "OPEN" ? `open · unrealised ${money(r.paper.unrealized_pnl)}`
                              : `${hm(r.paper.exit_time)} ${r.paper.exit_reason ?? ""}`) : "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: tone(r.paper?.net_pnl) }}>{money(r.paper?.net_pnl)}</TableCell>
                          <TableCell align="right" sx={{ color: tone(r.net_pnl_diff) }}>{money(r.net_pnl_diff)}</TableCell>
                        </TableRow>
                      ))}
                      {rows.length === 0 && (
                        <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No trades in this range.</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary">
                  A paper trade is paired with the backtest trade of the same side whose signal bar is within one bar. Click a row to
                  bring it into view on the chart.
                </Typography>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <b>On TradingView:</b> click &quot;TradingView script&quot;, open the Pine Editor on an NSE:{chart.index} 5-minute chart, paste
              the file&apos;s contents, then &quot;Add to chart&quot;. The script is a read-only indicator (no orders, no alerts) with the
              same markers, DEMA lines, WMA21 slope shading and a summary table; download it again to refresh the trades.
            </Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
